import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
  Box,
  Paper,
  Divider,
} from "@mui/material";

import Close from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";

import axios from "axios";
import { savePrecautiondb } from "../BaseURL"; 

const SpecialPrecautionDialog = ({ open, onClose, patient }) => {
  const [precautionType, setPrecautionType] = useState("");
  const [precautionNote, setPrecautionNote] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [loadingSave, setLoadingSave] = useState(false);

  // ✅ Load existing precaution when dialog opens
  useEffect(() => {
    if (open && patient) {
      if (patient.PrecautionType && patient.PrecautionType.trim() !== '') {
        setPrecautionType(patient.PrecautionType || "");
        setPrecautionNote(patient.precautionNote || "");
        setIsEditMode(false); // Start in view mode if precaution exists
      } else {
        // No precaution exists, start in edit mode
        setPrecautionType("");
        setPrecautionNote("");
        setIsEditMode(true);
      }
    }
  }, [open, patient]);

  // Check if precaution exists
  const hasPrecaution = patient?.PrecautionType && patient.PrecautionType.trim() !== '';

  // -----------------------------------------------
  // 🟧 SAVE FUNCTION
  // -----------------------------------------------
  const savePrecaution = async () => {
    if (!precautionType) {
      setSnackbarMessage("❌ Please select a precaution type");
      setSnackbarOpen(true);
      return;
    }

    setLoadingSave(true);

    try {
      const res = await axios.post(savePrecautiondb, {
        patientId: patient?.id,
        precautionType,
        precautionNote,
      });

      if (res.data.success) {
        setSnackbarMessage("✅ Precaution saved successfully");
        setSnackbarOpen(true);

        // Update patient data in parent component
        if (patient) {
          patient.PrecautionType = precautionType;
          patient.precautionNote = precautionNote;
        }

        setIsEditMode(false);

        // Close dialog after save
        setTimeout(() => {
          onClose();
        }, 1000);
      } else {
        setSnackbarMessage("❌ Failed to save precaution");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error(error);
      setSnackbarMessage("❌ Error saving precaution");
      setSnackbarOpen(true);
    }

    setLoadingSave(false);
  };

  const handleClose = () => {
    // Reset to original values when closing
    if (hasPrecaution) {
      setPrecautionType(patient.PrecautionType);
      setPrecautionNote(patient.precautionNote || "");
    } else {
      setPrecautionType("");
      setPrecautionNote("");
    }
    setIsEditMode(false);
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: hasPrecaution ? '0 8px 32px rgba(255, 152, 0, 0.3)' : '0 4px 20px rgba(0,0,0,0.1)',
            border: hasPrecaution ? '3px solid #FF9800' : 'none'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: hasPrecaution 
              ? "linear-gradient(135deg, #d61a1aff 0%, #F57C00 100%)"
              : "linear-gradient(135deg,  #d61a1aff 0%, #F57C00 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 700,
            fontSize: '1.25rem'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {hasPrecaution ? (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: '50%',
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                ⚠️
              </Box>
            ) : (
              <Box sx={{ fontSize: '1.5rem' }}>📋</Box>
            )}
            {hasPrecaution ? 'SPECIAL PRECAUTION' : 'Add Special Precaution'}
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3, mt: 2 }}>
          {/* Patient Info */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 2,
              border: '1px solid', borderColor: 'divider'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>Patient:</strong> {patient?.fullName} | 
              <strong> ID:</strong> {patient?.id} | 
              <strong> Age:</strong> {patient?.age}
            </Typography>
          </Box>

          {/* VIEW MODE - Show existing precaution */}
          {hasPrecaution && !isEditMode ? (
            <Box>
              {/* Precaution Type Display */}
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  mb: 3,
                  background: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
                  borderLeft: '6px solid #FF9800'
                }}
              >
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}
                >
                  Precaution Type
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#E65100',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 1
                  }}
                >
                  {precautionType}
                </Typography>
              </Paper>

              {/* Precaution Note Display */}
              {precautionNote && precautionNote.trim() !== '' && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
                    borderLeft: '6px solid #F44336'
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, display: 'block' }}
                  >
                    Important Notes
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#C62828',
                      fontWeight: 500,
                      lineHeight: 1.8,
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {precautionNote}
                  </Typography>
                </Paper>
              )}
            </Box>
          ) : (
            // EDIT MODE - Show form fields
            <Box>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Precaution Type *</InputLabel>
                <Select
                  value={precautionType}
                  label="Precaution Type *"
                  onChange={(e) => setPrecautionType(e.target.value)}
                >
                  <MenuItem value="Isolation">🚫 Isolation</MenuItem>
                  <MenuItem value="Airborne Precaution">💨 Airborne Precaution</MenuItem>
                  <MenuItem value="Droplet Precaution">💧 Droplet Precaution</MenuItem>
                  <MenuItem value="Contact Precaution">🤝 Contact Precaution</MenuItem>
                  <MenuItem value="Neutropenic Precaution">🛡️ Neutropenic Precaution</MenuItem>
                  <MenuItem value="Fall Risk">⚠️ Fall Risk</MenuItem>
                  <MenuItem value="Allergy Alert">🔴 Allergy Alert</MenuItem>
                  <MenuItem value="Infection Risk">🦠 Infection Risk</MenuItem>
                  <MenuItem value="Bleeding Risk">🩸 Bleeding Risk</MenuItem>
                  <MenuItem value="DNR (Do Not Resuscitate)">❌ DNR</MenuItem>
                  <MenuItem value="Seizure Precaution">⚡ Seizure Precaution</MenuItem>
                  <MenuItem value="Suicide Risk">🚨 Suicide Risk</MenuItem>
                  <MenuItem value="Other">📝 Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Additional Notes"
                multiline
                rows={4}
                value={precautionNote}
                onChange={(e) => setPrecautionNote(e.target.value)}
                placeholder="Enter additional notes..."
                sx={{ mb: 2 }}
              />

              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600}>
                  Important Notice
                </Typography>
                <Typography variant="caption">
                  This precaution will be visible on the patient's profile to all medical staff.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'background.default' }}>
          {hasPrecaution && !isEditMode ? (
            // VIEW MODE BUTTONS
            <>
              <Button
                onClick={handleClose}
                variant="contained"
                size="large"
                sx={{
                  bgcolor: '#FF9800',
                  '&:hover': { bgcolor: '#F57C00' },
                  px: 4,
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                I UNDERSTAND - PROCEED WITH CAUTION
              </Button>
              <Button
                onClick={() => setIsEditMode(true)}
                variant="outlined"
                color="warning"
                startIcon={<EditIcon />}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Edit Precaution
              </Button>
            </>
          ) : (
            // EDIT MODE BUTTONS
            <>
              <Button
                onClick={handleClose}
                variant="outlined"
                color="error"
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>

              <Button
                onClick={savePrecaution}
                variant="contained"
                color="warning"
                startIcon={<SaveIcon />}
                disabled={!precautionType || loadingSave}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {loadingSave ? "Saving..." : "Save Precaution"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbarMessage.includes("❌") ? "error" : "success"}
          sx={{ fontWeight: 600 }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SpecialPrecautionDialog;